import React from 'react';
import {
  Button,
  Input,
  Loader,
  Menu,
  MenuItemProps,
  Popover,
  useToasts,
} from 'freemium-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import * as Yup from 'yup';
import { AccountInfoType } from 'shared/types';
import Icon from '../../../../../shared/components/Icon';
import Spacer from '../../../../../shared/components/Spacer';
import {
  useGetAccountInfo,
  usePostAddDkim,
  useUpdateAccountInfo,
} from '../../../../../shared/hooks';
import {
  Center,
  FormContainer,
  FormContent,
  FormHeader,
  FreshLink,
} from '../../../../../shared/styles';
import { useDeleteDkim, useGetDkimList, useVerifyDKIM } from '../../hooks';
import { Col, DKIMTableRow, IconButton, Row, Status, Tag } from './styles';
import DkimRecordsView from './detailView';

const defaultDkimDomain = {
  domain: 'fwfreesurveys.com',
  isDefault: true,
  verified: true,
};

const DkimSettings = () => {
  const { t } = useTranslation();
  const [, setToast] = useToasts();
  const [anchorEl, setAnchorEl] = React.useState<any | null>(null);
  const [isNew, setIsNew] = React.useState<boolean>(false);
  const [isDkimAdded, setIsDkimAdded] = React.useState<boolean>(false);
  const [dkimList, setDkimList] = React.useState<any>([defaultDkimDomain]);
  const { data: accountInfo, isFetching } = useGetAccountInfo({});
  const {
    data: dkiminfo,
    mutate: createDkim,
    isLoading: isCreatingDkim,
  } = usePostAddDkim({
    callback: () => {
      setIsDkimAdded(true);
    },
  });
  const { data: dkimData, isLoading: dkimLoading } = useGetDkimList({
    enabled:
      Boolean(accountInfo?.dkim_domain) && !isDkimAdded && !isCreatingDkim,
  });

  const { mutate: updateAccount, isLoading } = useUpdateAccountInfo({
    setToast,
    callback: () => {
      if (isNew) createDkim();
    },
  });

  const { mutate: deleteDkim } = useDeleteDkim({
    setToast,
    callback: (isSuccess: boolean) => {
      if (isSuccess) {
        updateAccount({
          dkim_domain: null,
        });
        setDkimList([defaultDkimDomain]);
        reset();
      }
    },
  });
  const { mutate: verifyDkim } = useVerifyDKIM({ setToast });

  const menuOptions = [
    {
      id: 'remove',
      name: 'contacts.menu.remove.text',
    },
  ];

  const urlRegex =
    /(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9]{0,61}[a-z]/i;

  const form = useForm<AccountInfoType>({
    resolver: yupResolver(
      Yup.object({
        dkim_domain: Yup.string()
          .trim()
          .matches(urlRegex, `${t('forms.err_msg.valid_url')}`),
      }).required(),
    ),
    defaultValues: {
      dkim_domain: '',
    },
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = form;

  const onSubmit = (data: AccountInfoType) => {
    updateAccount(data);
    setIsNew(false);
  };

  const handleOptions = (selected: MenuItemProps) => {
    setAnchorEl(null);
    if (selected.id === 'verify') {
      verifyDkim();
    }
    if (selected.id === 'remove') {
      deleteDkim();
    }
  };

  React.useEffect(() => {
    if (accountInfo?.dkim_domain) {
      setDkimList([
        defaultDkimDomain,
        {
          domain: accountInfo?.dkim_domain,
          isDefault: false,
          verified: dkimData?.verified || null,
        },
      ]);
    }
  }, [accountInfo, dkimData]);

  return (
    <>
      <FormContainer>
        <FormHeader hideBorder>
          <div className="left-pane dkim-tooltip">
            <div>
              <div className="heading">
                {t('settings.dkim_settings.title')}{' '}
                {/* <Tooltip label={`${t('tooltip.import')}`} position="top">
                  <FreshLink
                    href="https://support.freshsurvey.io/en/support/solutions/articles/50000004072-e-how-to-configure-dkim-settings-"
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Icon width="16" height="14" glyph="question" fill="none" />
                  </FreshLink>
                </Tooltip> */}
              </div>
              <div className="sub-heading">
                {`${t('settings.dkim_settings.sub_heading')}  `}
                <FreshLink
                  href="https://support.freshsurvey.io/en/support/solutions/articles/50000004072-e-how-to-configure-dkim-settings-"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('settings.dkim_settings.link_text')}
                </FreshLink>
              </div>
              <div className="sub-heading"></div>
            </div>
          </div>
          <div className="right-pane">
            <Button
              inline
              disabled={Boolean(accountInfo?.dkim_domain)}
              onClick={(event: React.MouseEvent) => {
                event.preventDefault();
                setIsNew(true);
              }}
            >
              {t('settings.dkim_settings.add_dkim')}
            </Button>
          </div>
        </FormHeader>
        <FormContent>
          <Row className="header">
            <Col>{t('domain')}</Col>
          </Row>
          {dkimList.map((dkimDomainInfo: any) => (
            <DKIMTableRow key={dkimDomainInfo.domain}>
              <Row>
                <Col isDefault={dkimDomainInfo.isDefault}>
                  {dkimDomainInfo.isDefault ? (
                    <>
                      {dkimDomainInfo.domain}
                      <Tag>{t('settings.custom_fields.default')}</Tag>
                    </>
                  ) : (
                    <div>{dkimDomainInfo.domain}</div>
                  )}
                </Col>
                <Col>
                  {!dkimDomainInfo.isDefault && !dkimDomainInfo?.verified ? (
                    <>
                      <Button
                        type="secondary"
                        inline
                        onClick={(
                          e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
                        ) => {
                          e.preventDefault();
                          verifyDkim();
                        }}
                      >
                        {t('settings.custom_fields.verify')}
                      </Button>
                      <Spacer x={1} />
                    </>
                  ) : null}
                  {!dkimLoading ? (
                    <Status
                      className={
                        dkimDomainInfo?.verified ? 'verified' : 'pending'
                      }
                    >
                      {dkimDomainInfo?.verified
                        ? `${t('verified')}`
                        : `${t('pending')}`}
                    </Status>
                  ) : (
                    <Skeleton width={120} height={30} />
                  )}

                  {!dkimDomainInfo.isDefault ? (
                    <>
                      <Spacer x={2} />
                      <IconButton
                        aria-describedby="dkim-default"
                        cursor="pointer"
                        onClick={(e: MouseEvent) =>
                          setAnchorEl(e.currentTarget)
                        }
                      >
                        <Icon width="4" height="16" glyph="optiondot" />
                      </IconButton>
                      <Popover
                        id="dkim-default"
                        anchorEl={anchorEl}
                        onClose={() => setAnchorEl(null)}
                        anchorOrigin={{
                          vertical: 'top',
                          horizontal: 'left',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                      >
                        <Menu
                          options={menuOptions.map((o) => ({
                            ...o,
                            name: `${t(o.name)}`,
                          }))}
                          onSelect={(v: MenuItemProps) => {
                            handleOptions(v);
                          }}
                        />
                      </Popover>
                    </>
                  ) : (
                    <Spacer x={3} />
                  )}
                </Col>
              </Row>
              {!dkimDomainInfo.isDefault ? (
                <DkimRecordsView dkimData={dkiminfo ?? dkimData} />
              ) : null}
            </DKIMTableRow>
          ))}
          {isFetching || isLoading ? (
            <Center>
              <Loader />
            </Center>
          ) : null}
          {isNew ? (
            <form>
              <DKIMTableRow>
                <Row>
                  <Col>
                    <Input
                      placeholder="companydomain.com"
                      errorText={errors?.dkim_domain?.message}
                      isErrored={Boolean(errors.dkim_domain?.message)}
                      {...register('dkim_domain')}
                      autoFocus
                    />
                  </Col>
                  <Col>
                    <Button
                      inline
                      size="mini"
                      type="primary"
                      onClick={(event: React.MouseEvent) => {
                        event.preventDefault();
                        handleSubmit(onSubmit, (err) =>
                          console.log('ERR', err),
                        )();
                      }}
                    >
                      {t('save')}
                    </Button>
                    <Button
                      inline
                      size="mini"
                      type="secondary"
                      onClick={() => {
                        setIsNew(!isNew);
                        reset();
                      }}
                    >
                      {t('cancel')}
                    </Button>
                  </Col>
                </Row>
              </DKIMTableRow>
            </form>
          ) : null}
        </FormContent>
      </FormContainer>
    </>
  );
};

export default DkimSettings;
